// apply-tool-manifest-to-settings.ts
//
// Áp / rút manifest của 1 tool toggle (vd prompt-scoring) vào project
// settings.json. Đối xứng: enable = merge, disable = remove ĐÚNG những gì manifest
// khai. Nguồn sự thật là tool.json (KHÔNG nhúng marker `_feature` vào settings
// → settings giữ schema chuẩn 100%).
//
// Manifest sống ở: <project>/.claude/pack/tools/<name>/tool.json
// (submodule mount tại .claude/pack/). Manifest.settings chỉ chứa subset cần cho
// tool: hooks (per-event) + permissions.deny.
//
// Khác mergePackSettingsIntoProjectSettings (chỉ áp template + có statusLine/env/
// model): file này CHỈ lo hooks + deny của 1 tool, và biết RÚT ngược lại.
//
// Safety: backup settings.json trước mọi thay đổi; atomic write; idempotent
// (enable lại không nhân đôi; disable tool chưa bật → no-op).

import { promises as fs } from "node:fs";
import { join } from "node:path";
import { pathExists, readJson, writeJsonAtomic } from "./filesystem-helpers.js";
import {
  backupFilename,
  isCommandPathWithinWorkspace,
  type PackSettingsTemplate,
  unionDedupe,
} from "./merge-pack-settings-into-project-settings.js";
import { withSettingsFileLock } from "./with-settings-file-lock.js";
import { SECRET_FILE_MODE } from "./write-claude-settings-json-per-project.js";

// Một hook entry trong settings: { matcher?, hooks: [{type, command}] }.
interface HookEntry {
  matcher?: string;
  hooks?: Array<{ type?: string; command?: string }>;
}

export interface ToolManifest {
  name: string;
  version: string;
  description?: string;
  requires?: { runtime?: string };
  config?: Record<string, string>; // NEW: env vars bridged into settings.env
  settings: {
    hooks?: Record<string, HookEntry[]>;
    permissions?: { deny?: string[]; allow?: string[] };
  };
}

export interface ApplyToolResult {
  action: "enabled" | "disabled" | "no-change" | "no-manifest";
  backupPath?: string;
  changes: string[];
}

export function featureManifestPath(workspacePath: string, name: string): string {
  return join(workspacePath, ".claude", "pack", "tools", name, "tool.json");
}

// Đọc manifest tool. Thiếu HOẶC JSON hỏng → null (caller fail mềm: "pack chưa
// hỗ trợ tool"). try/catch tránh crash `avatar tool enable/disable` khi tool.json
// corrupt/ghi dở (đặc biệt sau pack-upgrade thay file).
export async function readToolManifest(
  workspacePath: string,
  name: string,
): Promise<ToolManifest | null> {
  const path = featureManifestPath(workspacePath, name);
  if (!(await pathExists(path))) {
    return null;
  }
  try {
    return await readJson<ToolManifest>(path);
  } catch {
    return null;
  }
}

const PROJECT_SETTINGS_REL = [".claude", "settings.json"];

function projectSettingsPath(workspacePath: string): string {
  return join(workspacePath, ...PROJECT_SETTINGS_REL);
}

// So khớp 2 hook entry theo (matcher + danh sách command) — định danh dùng cho
// cả dedupe (enable) lẫn remove (disable).
function hookEntryKey(entry: HookEntry): string {
  const commands = (entry.hooks ?? [])
    .map((h) => h.command ?? "")
    .sort()
    .join(" ");
  return `${entry.matcher ?? ""}${commands}`;
}

async function loadProjectSettings(
  workspacePath: string,
): Promise<{ settings: PackSettingsTemplate; existed: boolean }> {
  const path = projectSettingsPath(workspacePath);
  if (!(await pathExists(path))) {
    return { settings: {}, existed: false };
  }
  try {
    return { settings: await readJson<PackSettingsTemplate>(path), existed: true };
  } catch (err) {
    throw new Error(
      `Project settings.json không parse được: ${(err as Error).message}. Manual fix trước khi enable/disable tool.`,
    );
  }
}

async function backupAndWrite(
  workspacePath: string,
  settings: PackSettingsTemplate,
  existed: boolean,
): Promise<string | undefined> {
  const path = projectSettingsPath(workspacePath);
  let backupPath: string | undefined;
  if (existed) {
    backupPath = backupFilename(path);
    await fs.copyFile(path, backupPath);
  }
  // #164 chốt (f): dùng CHUNG cho enableTool (#2) + disableTool (#3) ⇒ chỉ MỘT lời
  // gọi phải truyền mode cho cả hai writer. Không truyền = mỗi lần bật/tắt tool lại
  // hạ settings.json (chứa API key) xuống 0644 theo umask.
  await writeJsonAtomic(path, settings, SECRET_FILE_MODE);
  return backupPath;
}

// v1.18.0: Normalize command để detect stale relative-path entries cần migrate.
// Strip ${CLAUDE_PROJECT_DIR}/ prefix → so sánh logical-equivalent giữa old + new.
function normalizeHookEntryForMigration(entry: HookEntry): string {
  const commands = (entry.hooks ?? [])
    .map((h) => (h.command ?? "").replace(/\$\{CLAUDE_PROJECT_DIR\}\//g, "").trim())
    .sort()
    .join("|");
  return `${entry.matcher ?? ""}::${commands}`;
}

function entryUsesProjectDirVar(entry: HookEntry): boolean {
  return (entry.hooks ?? []).some((h) => (h.command ?? "").includes("${CLAUDE_PROJECT_DIR}"));
}

// v1.18.0 MIGRATION: Trong cùng event, nếu user settings có entry old (relative
// path) VÀ tool manifest mang entry new (${CLAUDE_PROJECT_DIR}/) logical-tương-đương,
// drop entry old để chỉ giữ new sau merge. Fix cho upgrade từ pack pre-v0.7.1.
function migrateStaleEntries(
  userEntries: HookEntry[],
  featureEntries: HookEntry[],
): { migratedUser: HookEntry[]; droppedCount: number } {
  // Build set of normalized keys mà tool đang ship (đang là new version)
  const featureNormalized = new Set<string>();
  for (const fe of featureEntries) {
    if (entryUsesProjectDirVar(fe)) {
      featureNormalized.add(normalizeHookEntryForMigration(fe));
    }
  }
  if (featureNormalized.size === 0) {
    return { migratedUser: userEntries, droppedCount: 0 };
  }
  let dropped = 0;
  const migratedUser = userEntries.filter((ue) => {
    if (entryUsesProjectDirVar(ue)) return true; // keep new versions
    // Old version — drop nếu tool đang ship new logical-equivalent
    if (featureNormalized.has(normalizeHookEntryForMigration(ue))) {
      dropped++;
      return false;
    }
    return true;
  });
  return { migratedUser, droppedCount: dropped };
}

// ENABLE: merge hooks (append + dedupe theo matcher+command) + union deny.
// v1.18.0: Auto-migrate stale relative-path entries của cùng tool trong user
// settings — drop old keep new. Trigger khi user upgrade pack từ pre-v0.7.1.
export async function enableTool(
  workspacePath: string,
  manifest: ToolManifest,
): Promise<ApplyToolResult> {
  // #164 writer #2 — writer NÓNG NHẤT: `avatar sync` gọi qua enableToolByName ×N tool.
  // Bọc ở tầng này (không ở tầng enableToolByName) nên phủ một lượt cả 4 đường vào
  // (reapply-after-sync, tools.ts, tools-add-remove-runner, setup-default-tools-on-init)
  // mà KHÔNG giữ lock qua picker interactive của ai-orchestrator.
  return await withSettingsFileLock(workspacePath, () =>
    enableToolUnderLock(workspacePath, manifest),
  );
}

// Thân cũ của enableTool — dịch nguyên văn, KHÔNG đổi logic.
async function enableToolUnderLock(
  workspacePath: string,
  manifest: ToolManifest,
): Promise<ApplyToolResult> {
  const { settings, existed } = await loadProjectSettings(workspacePath);
  const merged: PackSettingsTemplate = { ...settings };
  const changes: string[] = [];

  // SECURITY: reject hook command trỏ path RA NGOÀI workspace (absolute / `../`)
  // trước khi merge — chốt chặn supply chain nếu tool.json bị compromise inject
  // hook trỏ `/malicious/bin` hoặc `../../evil`. Đối xứng với check statusLine.
  for (const [event, featEntries] of Object.entries(manifest.settings.hooks ?? {})) {
    for (const entry of featEntries) {
      for (const h of entry.hooks ?? []) {
        if (!h.command) continue;
        const check = isCommandPathWithinWorkspace(workspacePath, h.command);
        if (!check.safe) {
          throw new Error(
            `Tool "${manifest.name}" hook (${event}) bị từ chối: ${check.reason}. ` +
              `Hook pack PHẢI dùng path tương đối tới workspace (vd \${CLAUDE_PROJECT_DIR}/.claude/...).`,
          );
        }
      }
    }
  }

  // Hooks per-event: migrate stale + append entries tool, dedupe theo key.
  const mHooks = manifest.settings.hooks ?? {};
  if (Object.keys(mHooks).length > 0) {
    const userHooks = (settings.hooks ?? {}) as Record<string, HookEntry[]>;
    const outHooks: Record<string, HookEntry[]> = { ...userHooks };
    const touched: string[] = [];
    let totalMigrated = 0;
    for (const [event, featEntries] of Object.entries(mHooks)) {
      const existing = userHooks[event] ?? [];
      // v1.18.0: Drop stale relative-path versions của entries tool đang re-ship.
      const { migratedUser, droppedCount } = migrateStaleEntries(existing, featEntries);
      if (droppedCount > 0) {
        totalMigrated += droppedCount;
      }
      const seen = new Set(migratedUser.map(hookEntryKey));
      const toAdd = featEntries.filter((e) => !seen.has(hookEntryKey(e)));
      if (toAdd.length > 0 || droppedCount > 0) {
        outHooks[event] = [...migratedUser, ...toAdd];
        touched.push(event);
      }
    }
    if (touched.length > 0) {
      merged.hooks = outHooks as Record<string, unknown[]>;
      changes.push(`hooks added: ${touched.join(", ")}`);
      if (totalMigrated > 0) {
        changes.push(
          `migrated ${totalMigrated} stale relative-path entries → \\${"{"}CLAUDE_PROJECT_DIR\\${"}"} version`,
        );
      }
    }
  }

  // permissions.deny: union + dedupe.
  const featDeny = manifest.settings.permissions?.deny ?? [];
  if (featDeny.length > 0) {
    const userDeny = settings.permissions?.deny ?? [];
    const unionDeny = unionDedupe(userDeny, featDeny);
    if (unionDeny.length !== userDeny.length) {
      merged.permissions = { ...settings.permissions, deny: unionDeny };
      changes.push(`deny +${unionDeny.length - userDeny.length}`);
    }
  }

  // config → env: bơm env var tool khai (HUB_URL, AVATAR_KH_ENABLED) vào settings.env.
  // Add-if-absent (đồng bộ flow env cũ ở mergePackSettingsIntoProjectSettings):
  // user/override thắng — chỉ thêm key CHƯA có (không đè HUB_URL user tự set).
  const featConfig = manifest.config ?? {};
  if (Object.keys(featConfig).length > 0) {
    const userEnv = (settings.env ?? {}) as Record<string, string>;
    const mergedEnv: Record<string, string> = { ...userEnv };
    const addedKeys: string[] = [];
    for (const [k, v] of Object.entries(featConfig)) {
      if (!(k in mergedEnv)) {
        mergedEnv[k] = v;
        addedKeys.push(k);
      }
    }
    if (addedKeys.length > 0) {
      merged.env = mergedEnv;
      changes.push(`env +${addedKeys.length} (${addedKeys.join(", ")})`);
    }
  }

  if (changes.length === 0) {
    return { action: "no-change", changes: [] };
  }
  const backupPath = await backupAndWrite(workspacePath, merged, existed);
  return { action: "enabled", backupPath, changes };
}

// DISABLE: rút ĐÚNG hook entries + deny patterns mà manifest khai. Giữ nguyên mọi
// thứ khác (scout-block, deny user tự thêm). Dọn mảng rỗng sau khi rút.
export async function disableTool(
  workspacePath: string,
  manifest: ToolManifest,
): Promise<ApplyToolResult> {
  // #164 writer #3 — KHÔNG nằm trên đường `avatar sync` (đi qua `avatar tools disable`),
  // nhưng đua trên ĐÚNG một file với writer đường sync ⇒ cùng bug, cùng bracket.
  return await withSettingsFileLock(workspacePath, () =>
    disableToolUnderLock(workspacePath, manifest),
  );
}

// Thân cũ của disableTool — dịch nguyên văn, KHÔNG đổi logic.
async function disableToolUnderLock(
  workspacePath: string,
  manifest: ToolManifest,
): Promise<ApplyToolResult> {
  const { settings, existed } = await loadProjectSettings(workspacePath);
  if (!existed) {
    return { action: "no-change", changes: [] };
  }
  // Build merged bằng cách OMIT key (không dùng `delete` — theo convention codebase
  // + lint noDelete). Bắt đầu từ bản sao không gồm hooks/permissions; gán lại nếu
  // còn nội dung sau khi rút.
  const { hooks: _h, permissions: _p, env: _env, ...rest } = settings;
  const merged: PackSettingsTemplate = { ...rest };
  const changes: string[] = [];

  // Hooks: xoá entry khớp key của tool, GIỮ entry khác (scout-block...).
  const mHooks = manifest.settings.hooks ?? {};
  const userHooks = (settings.hooks ?? {}) as Record<string, HookEntry[]>;
  const outHooks: Record<string, HookEntry[]> = {};
  const removed: string[] = [];
  for (const [event, entries] of Object.entries(userHooks)) {
    const featEntries = mHooks[event];
    if (!featEntries) {
      outHooks[event] = entries; // event không thuộc tool → giữ nguyên.
      continue;
    }
    const featKeys = new Set(featEntries.map(hookEntryKey));
    const kept = entries.filter((e) => !featKeys.has(hookEntryKey(e)));
    if (kept.length !== entries.length) {
      removed.push(event);
    }
    if (kept.length > 0) {
      outHooks[event] = kept; // còn entry khác → giữ; rỗng → bỏ key.
    }
  }
  if (Object.keys(outHooks).length > 0) {
    merged.hooks = outHooks as Record<string, unknown[]>;
  }
  if (removed.length > 0) {
    changes.push(`hooks removed: ${removed.join(", ")}`);
  }

  // Deny: chỉ xoá đúng patterns manifest đã thêm. KHÔNG xoá deny user tự thêm.
  const featDeny = new Set(manifest.settings.permissions?.deny ?? []);
  const userDeny = settings.permissions?.deny ?? [];
  const keptDeny = featDeny.size > 0 ? userDeny.filter((d) => !featDeny.has(d)) : userDeny;
  // Tái dựng permissions: giữ field khác (allow...), set deny nếu còn, OMIT nếu rỗng.
  if (settings.permissions) {
    const { deny: _d, ...permRest } = settings.permissions;
    const newPerms = { ...permRest, ...(keptDeny.length > 0 ? { deny: keptDeny } : {}) };
    if (Object.keys(newPerms).length > 0) {
      merged.permissions = newPerms;
    }
  }
  if (keptDeny.length !== userDeny.length) {
    changes.push(`deny -${userDeny.length - keptDeny.length}`);
  }

  // config → env: rút đúng key tool đã bơm & value CÒN KHỚP; GIỮ key khác
  // (PATH, ANTHROPIC_*, user tự thêm); OMIT env nếu rỗng sau khi rút. Không xoá
  // key user đã override sang giá trị khác (value mismatch → giữ).
  const featConfig = manifest.config ?? {};
  if (settings.env) {
    const keptEnv: Record<string, string> = {};
    let removed = 0;
    for (const [k, v] of Object.entries(settings.env)) {
      if (k in featConfig && featConfig[k] === v) {
        removed++;
        continue;
      }
      keptEnv[k] = v;
    }
    if (Object.keys(keptEnv).length > 0) {
      merged.env = keptEnv;
    }
    if (removed > 0) {
      changes.push(`env -${removed}`);
    }
  }

  if (changes.length === 0) {
    return { action: "no-change", changes: [] };
  }
  const backupPath = await backupAndWrite(workspacePath, merged, existed);
  return { action: "disabled", backupPath, changes };
}
